import { onMounted } from 'vue';
import { defineStore, storeToRefs } from 'pinia';

import { is3rdView } from '@eaphone/env';
import { Network } from '@eaphone/network/next.ts';
import { SentrySetUser } from '@eaphone/sentry';
import { Auth } from '@eaphone/storage';
import type { UserInfo } from '@eaphone/storage';

const url = 'passport/v2/user/info';

export function getUserInfoBy(userId: string) {
  return Network.custom.get(url, { searchParams: { userId } });
}

function getUserInfo() {
  return Network.custom.get(url);
}

function postUserInfo(json: object) {
  return Network.custom.patch(url, { json });
}

function avatarUpload(file: File, userId: string) {
  return Network.custom.file(`passport/v1/user/${userId}/avatar`, { file });
}

function forMe<T extends UserInfo>(
  current: UserInfo,
  { id, name, remark, ...rest }: T,
): T {
  return id === 'guest'
    ? ({ id, name: '临时访客', ...rest } as T)
    : id === current.userId && !is3rdView
      ? ({ id, name: '我', ...rest, avatarUrl: current.avatarUrl } as T)
      : ({ id, name: remark ?? name, ...rest } as T);
}

export const useProfileStore = defineStore('profile', {
  state: (): { data: UserInfo } => ({
    data: { ...Auth.info },
  }),
  actions: {
    forMe<T extends UserInfo>(item: T): T {
      return forMe<T>(this.data, item);
    },
    forMeList<T extends UserInfo>(list: T[]): T[] {
      return list.map((item) => forMe<T>(this.data, item));
    },
    setData(data: UserInfo) {
      this.data = data;

      SentrySetUser({
        id: data.userId,
        username: data.name,
      });

      Auth.info = data;
    },
    async fetchData() {
      return getUserInfo().then((data) => {
        this.setData(data);

        return data;
      });
    },
    async update(result: object) {
      return postUserInfo(result).then((data) => {
        this.setData(data);

        return data;
      });
    },
    async setAvatar(file: File) {
      const { userId } = this.data;

      if (userId) {
        return avatarUpload(file, userId).then(() => {
          this.fetchData();
        });
      }
    },
  },
});

export function useProfile() {
  const store = useProfileStore();

  const io = storeToRefs(store);

  onMounted(() => {
    if (!io.data.value.id) {
      store.fetchData().catch((error) => {
        console.error(error);
      });
    }
  });

  return io.data;
}
